import { useState } from 'react';
import { useSuperAdmin } from '../context/SuperAdminContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

const SuperAdminLogin = () => {
  const { login } = useSuperAdmin();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(credentials.username, credentials.password);
      toast.success('Welcome, Super Admin!');
      navigate('/super-admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4 sm:p-8">
      {/* Logo */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">
            TinyBank
          </h1>
          <p className="text-amber-400 text-sm font-medium">Super Admin</p>
        </div>
      </div>

      <Card className="w-full max-w-md shadow-2xl bg-slate-800/50 border-slate-700 backdrop-blur" data-testid="admin-login-card">
        <CardHeader className="text-center">
          <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-amber-400" />
          </div>
          <CardTitle className="text-white">Administrator Access</CardTitle>
          <CardDescription className="text-slate-400">
            Sign in to the super admin portal
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-300">Username</Label>
              <Input
                id="username"
                data-testid="admin-username"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                placeholder="Enter admin username"
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                data-testid="admin-password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                placeholder="Enter password"
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium"
              disabled={loading}
              data-testid="admin-login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Access Dashboard
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="mt-8 text-sm text-slate-500">
        Restricted Access - Authorized Personnel Only
      </p>
    </div>
  );
};

export default SuperAdminLogin;
